/*
 * Harlan theme JS. No framework, no dependencies, loaded with `defer`.
 *
 * Cart: every change goes through Shopify's AJAX Cart API with `sections: 'cart-drawer'`, and the drawer's
 * HTML comes back rendered by Shopify (Section Rendering API). So there is no client-side pricing at all:
 * the discount lines and totals in the drawer are the ones checkout charges.
 *
 * Tracking is not here: Shopify's Web Pixels emit product_viewed / product_added_to_cart / checkout_started
 * on their own, and the Meta app listens to those (gated on Shopify's consent banner).
 */
(() => {
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------- Cart drawer ---------- */

  const root = document.querySelector('[data-drawer]');
  const panel = root?.querySelector('.drawer');
  const onCartPage = !!document.querySelector('[data-cart-page]');
  let lastFocus = null;

  function openDrawer() {
    if (!root) return;
    lastFocus = document.activeElement;
    root.hidden = false;
    requestAnimationFrame(() => root.classList.add('open'));
    document.body.style.overflow = 'hidden';
    panel.focus();
  }

  function closeDrawer() {
    if (!root || root.hidden) return;
    root.classList.remove('open');
    document.body.style.overflow = '';
    setTimeout(() => (root.hidden = true), 280);
    lastFocus?.focus?.();
  }

  function renderDrawer(sections) {
    const html = sections?.['cart-drawer'];
    if (!html || !root) return;
    const fresh = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-drawer-content]');
    const current = root.querySelector('[data-drawer-content]');
    if (!fresh || !current) return;
    // Keep keyboard focus on the same control (e.g. the "+" button) across the re-render.
    const focusKey = document.activeElement?.dataset?.focusKey;
    current.replaceWith(fresh);
    if (focusKey) root.querySelector(`[data-focus-key="${CSS.escape(focusKey)}"]`)?.focus();
    const count = Number(fresh.dataset.count || 0);
    document.querySelectorAll('[data-cart-count]').forEach((el) => {
      el.textContent = String(count);
      el.hidden = count === 0;
    });
  }

  async function cartRequest(url, body) {
    const res = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ ...body, sections: 'cart-drawer', sections_url: window.location.pathname }),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.description || data.message || 'Nem sikerült a kosárba tenni.');
    return data;
  }

  function showCartError(message) {
    const el = root?.querySelector('[data-cart-error]');
    if (!el) return;
    el.textContent = message;
    el.hidden = false;
  }

  async function addToCart(id, quantity) {
    const data = await cartRequest('/cart/add.js', { items: [{ id: Number(id), quantity: Number(quantity) || 1 }] });
    renderDrawer(data.sections);
    openDrawer();
  }

  async function changeLine(key, quantity) {
    try {
      const data = await cartRequest('/cart/change.js', { id: key, quantity: Number(quantity) });
      if (onCartPage) return window.location.reload();
      renderDrawer(data.sections);
    } catch (e) {
      showCartError(e.message);
    }
  }

  document.addEventListener('click', (e) => {
    const opener = e.target.closest('[data-cart-open]');
    if (opener && root && !onCartPage) {
      e.preventDefault();
      return openDrawer();
    }

    const change = e.target.closest('[data-change]');
    if (change) {
      e.preventDefault();
      return changeLine(change.dataset.change, change.dataset.qty);
    }

    const add = e.target.closest('[data-add], [data-quick-add]');
    if (add) {
      add.disabled = true;
      addToCart(add.dataset.add || add.dataset.quickAdd, 1)
        .catch((err) => {
          openDrawer();
          showCartError(err.message);
        })
        .finally(() => (add.disabled = false));
      return;
    }

    if (e.target.closest('[data-close]')) closeDrawer();
  });

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') closeDrawer();
  });

  /* ---------- Product gallery ---------- */
  // Swiping is native CSS scroll-snap; JS only syncs dots/thumbs and drives the arrows.

  document.querySelectorAll('[data-gallery-set]').forEach((set) => {
    const track = set.querySelector('[data-track]');
    const targets = [...set.querySelectorAll('[data-go]')];
    const count = track.children.length;
    const active = () => Math.round(track.scrollLeft / Math.max(1, track.clientWidth));
    const go = (i) => {
      const idx = (i + count) % count;
      track.scrollTo({ left: idx * track.clientWidth, behavior: reduced ? 'auto' : 'smooth' });
    };

    let raf = 0;
    let last = 0;
    track.addEventListener(
      'scroll',
      () => {
        cancelAnimationFrame(raf);
        raf = requestAnimationFrame(() => {
          const i = active();
          if (i === last) return;
          last = i;
          targets.forEach((t) => (Number(t.dataset.go) === i ? t.setAttribute('aria-current', 'true') : t.removeAttribute('aria-current')));
        });
      },
      { passive: true },
    );
    targets.forEach((t) => t.addEventListener('click', () => go(Number(t.dataset.go))));
    set.querySelector('[data-prev]')?.addEventListener('click', () => go(active() - 1));
    set.querySelector('[data-next]')?.addEventListener('click', () => go(active() + 1));
    track.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowRight') go(active() + 1);
      if (e.key === 'ArrowLeft') go(active() - 1);
    });
  });

  /* ---------- Buy box ---------- */

  const box = document.querySelector('[data-buybox]');
  if (box) {
    const form = box.querySelector('[data-product-form]');
    const atc = box.querySelector('[data-atc]');
    const error = box.querySelector('[data-atc-error]');
    const sticky = document.querySelector('[data-sticky]');
    const stickyBtn = sticky?.querySelector('[data-atc-proxy]');
    const q = (sel) => box.querySelector(sel);
    const variant = () => form.querySelector('input[name="id"]:checked') || form.querySelector('input[name="id"]');
    const tier = () => form.querySelector('input[name="quantity"]:checked');
    const setText = (el, text) => el && (el.textContent = text);

    // Show only the element matching the colour in every [data-variant-only] group; if a group has no match,
    // keep its first element (e.g. one closing image shared by all colours).
    function showVariant(key) {
      const groups = new Set([...document.querySelectorAll('[data-variant-only]')].map((el) => el.parentElement));
      groups.forEach((group) => {
        const items = [...group.children].filter((el) => el.hasAttribute('data-variant-only'));
        const match = items.some((el) => el.dataset.variantOnly === key);
        items.forEach((el, i) => (el.hidden = match ? el.dataset.variantOnly !== key : i !== 0));
      });
      document.querySelectorAll('[data-gallery-set]:not([hidden]) [data-track]').forEach((t) => (t.scrollLeft = 0));
    }

    function update() {
      const v = variant();
      const t = tier();
      const qty = Number(t.value);
      const total = t.dataset.total;
      const saved = Number(t.dataset.savedRaw);
      const colour = v.dataset.name ? `${v.dataset.name} · ` : '';

      setText(q('[data-variant-label]'), v.dataset.name || '');
      setText(q('[data-price-total]'), total);
      setText(q('[data-atc-price]'), total);
      const save = q('[data-save]');
      save.hidden = saved === 0;
      save.textContent = `${t.dataset.saved} megtakarítás`;
      setText(sticky?.querySelector('[data-sticky-meta]'), `${colour}${qty} darab · ${total}`);
      document.querySelectorAll('[data-selection]').forEach((el) => (el.textContent = `${colour}${qty} darab`));
      document.querySelectorAll('[data-selection-price]').forEach((el) => (el.textContent = total));
    }

    form.querySelectorAll('input[name="id"][type="radio"]').forEach((input) =>
      input.addEventListener('change', () => {
        showVariant(input.dataset.key);
        const url = new URL(window.location.href);
        url.searchParams.set('variant', input.value);
        history.replaceState(null, '', url);
        update();
      }),
    );
    form.querySelectorAll('input[name="quantity"]').forEach((input) => input.addEventListener('change', update));

    form.addEventListener('submit', async (e) => {
      e.preventDefault();
      error.hidden = true;
      atc.disabled = true;
      try {
        await addToCart(variant().value, tier().value);
      } catch (err) {
        error.textContent = err.message;
        error.hidden = false;
      } finally {
        atc.disabled = false;
      }
    });
    document.querySelectorAll('[data-atc-proxy]').forEach((b) => b.addEventListener('click', () => form.requestSubmit()));

    // Sticky add-to-cart: appears once the main button has scrolled above the viewport.
    if (sticky && atc) {
      new IntersectionObserver(([entry]) => {
        const show = !entry.isIntersecting && entry.boundingClientRect.top < 0;
        sticky.classList.toggle('show', show);
        sticky.setAttribute('aria-hidden', String(!show));
        if (stickyBtn) stickyBtn.tabIndex = show ? 0 : -1;
      }).observe(atc);
    }

    update();
  }
})();
